import React, { useState, useEffect } from "react";
import { useTemplateStore } from "@/zustand/templateStore";
import { useProductData } from "@/zustand/productStore";
import { useCartData } from "@/zustand/cartStore.js";

const ApplePayButton = ({ height, onApplePaySubmit }) => {
  const [isAvailable, setIsAvailable] = useState(false);

  const setIsApplePayAvailable = useTemplateStore((s) => s.setIsApplePayAvailable); // Browser defines this
  const handlePaymentMethodChange = useTemplateStore((s) => s.handlePaymentMethodChange); // For Accordion Toggle
  const { dynamicDescriptor } = window.cc_payments || {};

  useEffect(() => {
    setIsApplePayAvailable(isAvailable);
  }, [isAvailable]);

  useEffect(() => {
    try {
      if (window.ApplePaySession && window.ApplePaySession.canMakePayments()) {
        setIsAvailable(true);
      }
    } catch (err) {
      console.error("Apple Pay availability check failed:", err);
    }
  }, []);

  if (!isAvailable) {
    return null;
  }
  const startApplePay = async () => {
    handlePaymentMethodChange("applepay"); // For Accordion Toggle

    const { currency_code, country_code } = useProductData.getState();
    const { cartTotal } = useCartData.getState();

    const paymentRequest = {
      currencyCode: currency_code || "USD",
      countryCode: country_code || "US",
      total: {
        label: dynamicDescriptor, // "Checkoutera",
        amount: cartTotal || "0.00",
      },

      supportedNetworks: ["visa", "masterCard", "amex", "discover", "JCB"],
      merchantCapabilities: ["supports3DS"],
      requiredShippingContactFields: ["email", "phone", "postalAddress", "name"],
    };

    const session = new ApplePaySession(3, paymentRequest);

    session.onvalidatemerchant = async (event) => {
      const r = await fetch("/wp-json/checkout/v1/order/applepay/validate", { method: "POST" });
      const merchantSession = await r.json();
      session.completeMerchantValidation(merchantSession);
    };

    session.oncancel = (event) => {
      // TODO: Add cancel handler. Maybe we can show modal or error message and let user know to try different payment method?
    };

    session.onpaymentauthorized = async (event) => {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => {
          // Max 30 seconds to pay, otherwise - cancel
          controller.abort();
        }, 30000);

        const applePayData = {
          apple_pay_token: event.payment.token,
          customer: {
            first_name: event.payment.shippingContact?.givenName || event.payment.billingContact?.givenName || "",
            last_name: event.payment.shippingContact?.familyName || event.payment.billingContact?.familyName || "",
            phone: event.payment.shippingContact?.phoneNumber || event.payment.billingContact?.phoneNumber || "",
            email: event.payment.shippingContact?.emailAddress || "",
            address: event.payment.shippingContact?.addressLines[0] || "",
            address2: event.payment.shippingContact?.addressLines[1] || "",
            city: event.payment.shippingContact?.locality || "",
            state: event.payment.shippingContact?.administrativeArea || "",
            zip: event.payment.shippingContact?.postalCode || "",
            country: event.payment.shippingContact?.countryCode || "",
          },
        };

        // Call the form handler with Apple Pay specific data
        if (onApplePaySubmit) {
          await onApplePaySubmit(applePayData, { signal: controller.signal });
          clearTimeout(timeout);

          session.completePayment(ApplePaySession.STATUS_SUCCESS);
          // Redirect to next page / upsell
        } else {
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          // TODO: Show error message
        }
      } catch (e) {
        console.error("Payment authorization error:", e);
        session.completePayment(ApplePaySession.STATUS_FAILURE);
        // TODO: Show error message (choose different payment method)
      }
    };

    session.begin();
  };

  return (
    <div className="applepay-container">
      <button
        type="button"
        onClick={startApplePay}
        style={{
          appearance: "-apple-pay-button",
          WebkitAppearance: "-apple-pay-button",
          applePayButtonStyle: "black",
          width: "100%",
          height: height || 56,
          borderRadius: 6,
          border: "none",
          cursor: "pointer",
        }}
      />
    </div>
  );
};

export default ApplePayButton;
